'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, TextField, Chip, Autocomplete } from '@mui/material';
import { Card } from 'flowbite-react';
import { Venue } from '../../../../shared/models';
import { getAttributeIcons } from '../../lib/firebase/metadata';
import { fetchAllVenueMetadata } from '../../lib/firebase/venues';

interface AttributeSelectionProps {
    data: Partial<Venue>;
    onNext: (venueData: Partial<Venue>) => void;
    place?: google.maps.places.PlaceResult | null;
    missingFields?: string[];
}

const AttributeSelection: React.FC<AttributeSelectionProps> = ({ data, onNext }) => {
    // Sanitize initial attributes from data
    const initialAttributes = Object.keys(data.Attributes || {})
        .filter(attr => typeof attr === 'string')
        .map(attr => String(attr));

    // Sanitize initial neighborhoods from data
    const initialNeighborhoods = (data.Neighborhoods || [])
        .filter(neighborhood => typeof neighborhood === 'string')
        .map(neighborhood => String(neighborhood));

    const [attributes, setAttributes] = useState<string[]>(initialAttributes);
    const [neighborhoods, setNeighborhoods] = useState<string[]>(initialNeighborhoods);
    const [allAttributes, setAllAttributes] = useState<string[]>([]);
    const [knownNeighborhoods, setKnownNeighborhoods] = useState<string[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [attributeInputValue, setAttributeInputValue] = useState('');
    const [neighborhoodInputValue, setNeighborhoodInputValue] = useState('');
    const [neighborhoodError, setNeighborhoodError] = useState<string>('');

    useEffect(() => {
        const fetchMetadata = async () => {
            const metadata = await fetchAllVenueMetadata();

            // Ensure attributes and neighborhoods are strings
            const sanitizedAttributes = (metadata.possibleAttributes || [])
                .filter(attr => typeof attr === 'string')
                .map(attr => String(attr));

            const sanitizedNeighborhoods = (metadata.possibleNeighborhoods || [])
                .filter(neighborhood => typeof neighborhood === 'string')
                .map(neighborhood => String(neighborhood));

            setAllAttributes(sanitizedAttributes);
            setKnownNeighborhoods(sanitizedNeighborhoods);
        };
        fetchMetadata();
    }, []);

    const handleAttributeChange = (_event: React.SyntheticEvent, newValue: string[]) => {
        setAttributes(Array.from(new Set(newValue)));
        setHasUnsavedChanges(true);
    };

    const handleAttributeInputChange = (_event: React.SyntheticEvent, newValue: string) => {
        setAttributeInputValue(newValue);
    };

    const handleAttributeKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && attributeInputValue.trim() !== '') {
            // Prevent form submission
            event.preventDefault();

            // Check if attribute already exists
            if (!attributes.includes(attributeInputValue.trim())) {
                setAttributes(prev => [...prev, attributeInputValue.trim()]);
                setHasUnsavedChanges(true);
                setAttributeInputValue('');
            }
        }
    };

    const handleNeighborhoodChange = (_event: React.SyntheticEvent, newValue: string[]) => {
        setNeighborhoods(Array.from(new Set(newValue)));
        setHasUnsavedChanges(true);
        setNeighborhoodError('');
    };

    const handleNeighborhoodInputChange = (_event: React.SyntheticEvent, newValue: string) => {
        setNeighborhoodInputValue(newValue);
    };

    const handleNeighborhoodKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && neighborhoodInputValue.trim() !== '') {
            // Prevent form submission
            event.preventDefault();

            // Check if neighborhood already exists
            if (!neighborhoods.includes(neighborhoodInputValue.trim())) {
                setNeighborhoods(prev => [...prev, neighborhoodInputValue.trim()]);
                setHasUnsavedChanges(true);
                setNeighborhoodInputValue('');
            }
        }
    };

    const handleSubmit = () => {
        // Validate neighborhoods
        if (!neighborhoods || neighborhoods.length === 0) {
            setNeighborhoodError('Please select at least one neighborhood');
            return;
        }

        const attributesObject = attributes.reduce((acc, attr) => ({ ...acc, [attr]: true }), {});
        onNext({
            ...data,
            Attributes: attributesObject,
            Neighborhoods: neighborhoods
        });
        setHasUnsavedChanges(false);
    };

    // Simplified chip style for selected items
    const selectedChipStyle = {
        m: 0.5,
        fontWeight: 500,
        textTransform: 'capitalize'
    };

    // Filter attributes that are not already selected
    const filteredAttributes = allAttributes.filter(attr => !attributes.includes(attr));
    // Always display all attributes
    const displayAttributes = filteredAttributes;

    return (
        <Card className={`mt-4 bg-gray-800 border-0 shadow-xl ${hasUnsavedChanges ? 'ring-2 ring-amber-400' : ''}`}>
            {/* Attributes Section */}
            <section className="mb-6">
                <div className="flex items-center mb-2">
                    <div className="bg-purple-600 w-2 h-6 mr-2 rounded"></div>
                    <Typography variant="h5" className="font-bold text-white">Venue Attributes</Typography>
                </div>

                <p className="text-gray-300 text-sm mb-3">
                    Select attributes that describe your venue. Type to create custom attributes and press Enter to add.
                </p>

                <Autocomplete
                    multiple
                    freeSolo
                    options={allAttributes.filter(attr => !attributes.includes(attr))}
                    value={attributes}
                    onChange={handleAttributeChange}
                    inputValue={attributeInputValue}
                    onInputChange={handleAttributeInputChange}
                    onKeyDown={handleAttributeKeyDown}
                    renderTags={(value: string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                            const tagProps = getTagProps({ index }) || {};
                            return (
                                <Chip
                                    variant="filled"
                                    label={String(option)}
                                    {...tagProps}
                                    key={`attr-${option}-${index}`}
                                    sx={{
                                        ...selectedChipStyle,
                                        backgroundColor: '#7e57c2',
                                        color: 'white',
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Attributes"
                            placeholder="Add attribute and press Enter"
                            className="bg-gray-700 rounded-lg"
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: { color: 'white' },
                                sx: {
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' }
                                }
                            }}
                        />
                    )}
                />

                {allAttributes.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-amber-300 font-medium text-sm">ALL AVAILABLE ATTRIBUTES</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {displayAttributes.map((attr, attrIndex) => (
                                <Chip
                                    key={`available-attr-${attr}-${attrIndex}`}
                                    label={String(attr)}
                                    onClick={() => {
                                        setAttributes((prev) => Array.from(new Set([...prev, attr])));
                                        setHasUnsavedChanges(true);
                                    }}
                                    sx={{
                                        backgroundColor: 'rgba(124, 77, 255, 0.2)',
                                        color: 'white',
                                        borderColor: 'rgba(124, 77, 255, 0.3)',
                                        m: 0.3,
                                        '&:hover': {
                                            backgroundColor: 'rgba(124, 77, 255, 0.4)',
                                        }
                                    }}
                                />
                            ))}
                            {displayAttributes.length === 0 && (
                                <span className="text-gray-400 text-sm italic">All attributes already selected</span>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <hr className="border-gray-700 my-4" />

            {/* Neighborhoods Section */}
            <section className="mb-6">
                <div className="flex items-center mb-2">
                    <div className="bg-amber-500 w-2 h-6 mr-2 rounded"></div>
                    <Typography variant="h5" className="font-bold text-white">Neighborhoods</Typography>
                </div>

                <p className="text-gray-300 text-sm mb-3">
                    Select neighborhoods where this venue is located or nearby. This helps with discovery. Type and press Enter to add custom neighborhoods.
                </p>

                <Autocomplete
                    multiple
                    freeSolo
                    options={knownNeighborhoods.filter(n => !neighborhoods.includes(n))}
                    value={neighborhoods}
                    onChange={handleNeighborhoodChange}
                    inputValue={neighborhoodInputValue}
                    onInputChange={handleNeighborhoodInputChange}
                    onKeyDown={handleNeighborhoodKeyDown}
                    renderTags={(value: string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                            const tagProps = getTagProps({ index }) || {};
                            return (
                                <Chip
                                    variant="filled"
                                    label={String(option)}
                                    {...tagProps}
                                    key={`neighborhood-${option}-${index}`}
                                    sx={{
                                        ...selectedChipStyle,
                                        backgroundColor: '#f59e0b',
                                        color: '#111827',
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Neighborhoods"
                            placeholder="Add neighborhood and press Enter"
                            className="bg-gray-700 rounded-lg"
                            error={!!neighborhoodError}
                            helperText={neighborhoodError}
                            FormHelperTextProps={{
                                sx: {
                                    color: '#ef4444',
                                    marginLeft: '14px',
                                    marginTop: '4px'
                                }
                            }}
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: { color: 'white' },
                                sx: {
                                    '& fieldset': {
                                        borderColor: neighborhoodError ? '#ef4444' : 'rgba(255,255,255,0.3)',
                                        borderWidth: neighborhoodError ? '2px' : '1px'
                                    }
                                }
                            }}
                        />
                    )}
                />

                <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
                    <p className="text-amber-300 font-medium mb-2 text-sm">ALL AVAILABLE NEIGHBORHOODS</p>
                    <div className="flex flex-wrap gap-1">
                        {knownNeighborhoods.filter(n => !neighborhoods.includes(n)).map((neighborhood, neighborhoodIndex) => (
                            <Chip
                                key={`available-neighborhood-${neighborhood}-${neighborhoodIndex}`}
                                label={String(neighborhood)}
                                onClick={() => {
                                    setNeighborhoods((prev) => Array.from(new Set([...prev, neighborhood])));
                                    setHasUnsavedChanges(true);
                                }}
                                sx={{
                                    backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                    color: 'white',
                                    borderColor: 'rgba(245, 158, 11, 0.3)',
                                    m: 0.3,
                                    '&:hover': {
                                        backgroundColor: 'rgba(245, 158, 11, 0.4)',
                                    }
                                }}
                            />
                        ))}
                        {knownNeighborhoods.filter(n => !neighborhoods.includes(n)).length === 0 && (
                            <span className="text-gray-400 text-sm italic">All neighborhoods already selected</span>
                        )}
                    </div>
                </div>
            </section>

            {/* Action Button */}
            <div className="flex items-center justify-end gap-4 mt-2">
                {hasUnsavedChanges && (
                    <span className="text-amber-400 text-sm animate-pulse">
                        Unsaved changes
                    </span>
                )}
                <Button
                    onClick={handleSubmit}
                    className={`
                        min-w-[160px] font-bold px-6 py-2.5 rounded-lg
                        ${hasUnsavedChanges
                            ? 'bg-amber-500 hover:bg-amber-600 text-gray-900'
                            : 'bg-purple-600 hover:bg-purple-700 text-white'}
                        transition-all duration-300
                        ${hasUnsavedChanges ? 'animate-pulse' : ''}
                    `}
                    variant="contained"
                >
                    {hasUnsavedChanges ? 'Save Changes' : 'Continue'}
                </Button>
            </div>
        </Card>
    );
};

export default AttributeSelection;